import React, { Suspense } from 'react';
import { Clock } from 'lucide-react';
import { PersistedWorkflow } from '@cotera/sdk/core';
import { humanReadableSchedule } from '@cotera/client/app/v1/trigger/schedule-utils';
import type {
  WorkflowCronTriggerViewData,
  WorkflowTriggerRenderStrategy,
  WorkflowTriggerViewData,
} from '../types';

const { PersistedWorkflowTriggerKind } = PersistedWorkflow;

const LazyCronTriggerBody = React.lazy(() =>
  import('./cron-trigger-body').then((mod) => ({
    default: mod.CronTriggerBody,
  }))
);

function isCronTrigger(
  trigger: WorkflowTriggerViewData
): trigger is WorkflowCronTriggerViewData {
  return trigger.kind === PersistedWorkflowTriggerKind.Cron;
}

export const cronTriggerStrategy: WorkflowTriggerRenderStrategy = {
  triggerKind: PersistedWorkflowTriggerKind.Cron,
  accent: 'trigger',
  badge: 'Schedule',
  matches: isCronTrigger,
  getTitle: (trigger) =>
    isCronTrigger(trigger) && trigger.name.trim() !== ''
      ? trigger.name
      : 'Schedule',
  getDescription: (trigger) => {
    if (!isCronTrigger(trigger)) {
      return undefined;
    }
    const human = humanReadableSchedule(trigger.cron);
    // Avoid repeating the schedule when the trigger name is already the schedule.
    return trigger.name.trim() === human ? undefined : human;
  },
  renderIcon: () => <Clock className="h-4 w-4" />,
  renderBody: (trigger) => {
    if (!isCronTrigger(trigger)) {
      return null;
    }
    return (
      <Suspense fallback={null}>
        <LazyCronTriggerBody trigger={trigger} />
      </Suspense>
    );
  },
};
